// Single source of truth for user roles on the frontend.
//
// Mirrors backend/lib/roles.js. Canonical roles are admin / managing_director /
// reservation_manager. Legacy strings (director, manager, agent, employee) still
// exist on older accounts and in a few allow-lists; they are kept explicit so
// access groups stay centralized instead of copy-pasted across components.
//
// Group membership is preserved verbatim from the previous inline arrays — do
// not change who is in a group as part of "tidying".

// --- Canonical roles ---
export const ROLES = Object.freeze({
  ADMIN: 'admin',
  MANAGING_DIRECTOR: 'managing_director',
  RESERVATION_MANAGER: 'reservation_manager',
});

// --- Legacy / non-canonical roles still present on existing accounts ---
// ('director' removed 2026-07-13: zero users carry it and no frontend gate
// granted it anything; the backend lib/roles.js groups still honor it — a
// separate backend cleanup decision.)
export const LEGACY_ROLES = Object.freeze({
  MANAGER: 'manager',
  AGENT: 'agent',
  EMPLOYEE: 'employee',
});

// --- Route / access groups (membership preserved verbatim) ---

// Management-only routes (settings, finance, reports).
export const MANAGEMENT_ROLES = Object.freeze([ROLES.ADMIN, ROLES.MANAGING_DIRECTOR]);

// Operational areas RM can use (chats, inbox, calendar, guest preferences…).
export const OPERATIONAL_ROLES = Object.freeze([
  ROLES.ADMIN,
  ROLES.MANAGING_DIRECTOR,
  ROLES.RESERVATION_MANAGER,
]);

// Admin-only (user management, audit log).
export const ADMIN_ONLY_ROLES = Object.freeze([ROLES.ADMIN]);

// Dashboard landing — includes legacy employee/manager so existing accounts keep
// access exactly as before.
export const DASHBOARD_ROLES = Object.freeze([
  LEGACY_ROLES.EMPLOYEE,
  LEGACY_ROLES.MANAGER,
  ROLES.ADMIN,
  ROLES.MANAGING_DIRECTOR,
  ROLES.RESERVATION_MANAGER,
]);

// Legacy manager dashboard route (kept for existing 'manager' accounts).
export const LEGACY_MANAGER_DASHBOARD_ROLES = Object.freeze([LEGACY_ROLES.MANAGER, ROLES.ADMIN]);

// Global (unscoped) access incl. the legacy manager alias.
// Source: lead assignment ("assign to others").
export const GLOBAL_ACCESS_ROLES = Object.freeze([
  ROLES.ADMIN,
  ROLES.MANAGING_DIRECTOR,
  LEGACY_ROLES.MANAGER,
]);

// --- Helpers ---

const roleOf = (userOrRole) =>
  typeof userOrRole === 'string' ? userOrRole : userOrRole?.role;

export const normalizeRole = (role) => String(role ?? '').trim().toLowerCase();

export const hasRole = (userOrRole, roles) => {
  const list = Array.isArray(roles) ? roles : [roles];
  const role = roleOf(userOrRole);
  return !!role && list.includes(role);
};

export const isAdmin = (userOrRole) => roleOf(userOrRole) === ROLES.ADMIN;

export const isManagement = (userOrRole) => hasRole(userOrRole, MANAGEMENT_ROLES);

export const isReservationManager = (userOrRole) =>
  roleOf(userOrRole) === ROLES.RESERVATION_MANAGER;
